import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react'
import type { SkuLinkedStockDraftRow } from 'services/interface/productLinkedStock.interface'
import {
  getSkuModalContentWidth,
  type SkuModalLayoutTab,
} from './skuModalConstants'

type SkuModalStockEditContextValue = {
  productId?: string | null
  modalLayoutTab: SkuModalLayoutTab
  contentWidth: number
  setModalLayoutTab: (tab: SkuModalLayoutTab) => void
  editTarget: SkuLinkedStockDraftRow | null
  ingredientPickerOpen: boolean
  pickerPanel: ReactNode | null
  openStockEdit: (row: SkuLinkedStockDraftRow) => void
  closeStockEdit: () => void
  openIngredientPicker: () => void
  closeIngredientPicker: () => void
  setPickerPanel: (node: ReactNode | null) => void
  registerOnStockSaved: (fn: () => void) => void
  notifyStockSaved: () => void
}

const SkuModalStockEditContext = createContext<SkuModalStockEditContextValue | null>(
  null
)

export const useSkuModalStockEdit = (): SkuModalStockEditContextValue => {
  const ctx = useContext(SkuModalStockEditContext)
  if (!ctx) {
    throw new Error('useSkuModalStockEdit must be used within SkuModalStockEditProvider')
  }
  return ctx
}

/** ใช้ใน ProductSKUCreate — ลงทะเบียน refresh รายการผูกหลังแก้สต็อก */
export const useSkuModalStockEditOptional = (): SkuModalStockEditContextValue | null =>
  useContext(SkuModalStockEditContext)

type SkuModalStockEditProviderProps = {
  productId?: string | null
  initialLayoutTab?: SkuModalLayoutTab
  /** สถานะเปิด/ปิด modal — Provider อยู่นอก destroyOnClose จึงต้อง reset layout เองเมื่อเปิด/ปิด */
  open?: boolean
  children: ReactNode
}

export const SkuModalStockEditProvider: FC<SkuModalStockEditProviderProps> = ({
  productId,
  initialLayoutTab = 'price',
  open = false,
  children,
}) => {
  const [modalLayoutTab, setModalLayoutTab] =
    useState<SkuModalLayoutTab>(initialLayoutTab)
  const contentWidth = getSkuModalContentWidth(modalLayoutTab)
  const [editTarget, setEditTarget] = useState<SkuLinkedStockDraftRow | null>(null)
  const [ingredientPickerOpen, setIngredientPickerOpen] = useState(false)
  const [pickerPanel, setPickerPanel] = useState<ReactNode | null>(null)
  const onStockSavedRef = useRef<(() => void) | undefined>(undefined)
  const prevOpenRef = useRef(open)

  useEffect(() => {
    if (prevOpenRef.current === open) return
    prevOpenRef.current = open

    setModalLayoutTab(initialLayoutTab)
    setEditTarget(null)
    setIngredientPickerOpen(false)
    setPickerPanel(null)
  }, [open, initialLayoutTab])

  const closeIngredientPicker = useCallback(() => {
    setIngredientPickerOpen(false)
    setPickerPanel(null)
  }, [])

  const closeStockEdit = useCallback(() => {
    setEditTarget(null)
  }, [])

  const openStockEdit = useCallback((row: SkuLinkedStockDraftRow) => {
    setEditTarget(row)
  }, [])

  const openIngredientPicker = useCallback(() => {
    setIngredientPickerOpen(true)
  }, [])

  const registerOnStockSaved = useCallback((fn: () => void) => {
    onStockSavedRef.current = fn
  }, [])

  const notifyStockSaved = useCallback(() => {
    onStockSavedRef.current?.()
  }, [])

  const value = useMemo(
    () => ({
      productId,
      modalLayoutTab,
      contentWidth,
      setModalLayoutTab,
      editTarget,
      ingredientPickerOpen,
      pickerPanel,
      openStockEdit,
      closeStockEdit,
      openIngredientPicker,
      closeIngredientPicker,
      setPickerPanel,
      registerOnStockSaved,
      notifyStockSaved,
    }),
    [
      productId,
      modalLayoutTab,
      contentWidth,
      editTarget,
      ingredientPickerOpen,
      pickerPanel,
      openStockEdit,
      closeStockEdit,
      openIngredientPicker,
      closeIngredientPicker,
      registerOnStockSaved,
      notifyStockSaved,
    ]
  )

  return (
    <SkuModalStockEditContext.Provider value={value}>
      {children}
    </SkuModalStockEditContext.Provider>
  )
}
